import { useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Paper, Typography, makeStyles } from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  parseSystemVen,
  useSystemRegistryDocument,
  type VenIssue,
} from './useSystemRegistry';

export { parseSystemVen };
export type { VenIssue };

const useStyles = makeStyles(theme => ({
  code: {
    fontFamily: 'monospace',
    fontSize: 12,
    margin: 0,
    padding: theme.spacing(2),
    borderRadius: 4,
    background: '#1e1e1e',
    color: '#d4d4d4',
    maxHeight: '70vh',
    overflow: 'auto',
  },
  line: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  errorLine: {
    background: 'rgba(244,67,54,0.18)',
    color: '#ff8a80',
    borderLeft: '3px solid #f44336',
    marginLeft: -theme.spacing(2),
    paddingLeft: theme.spacing(2) - 3,
  },
  errorText: {
    color: '#ff5252',
    fontStyle: 'italic',
  },
  banner: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: theme.spacing(1, 2),
    marginBottom: theme.spacing(1),
    borderRadius: 4,
    background: 'rgba(244,67,54,0.12)',
    border: '1px solid rgba(244,67,54,0.4)',
    color: '#ff8a80',
  },
  tabBadge: {
    marginLeft: 6,
    fontWeight: 700,
  },
}));

/**
 * Pairs each rendered JSON line with the VEN issue it mentions, if any. Matching
 * on the quoted value avoids flagging a line that merely shares a prefix with a
 * broken VEN.
 */
export function annotateLines(
  json: string,
  issues: VenIssue[],
): Array<{ text: string; error?: string }> {
  return json.split('\n').map(text => ({
    text,
    error: issues.find(issue => text.includes(`"${issue.ven}"`))?.error,
  }));
}

/**
 * Tab label carrying the VEN health of the system: a red count of unresolved
 * references, or a green tick when everything resolves.
 */
export const SystemRegistryJsonTabLabel = () => {
  const { entity } = useEntity();
  const classes = useStyles();
  const coordinates = parseSystemVen(entity.metadata.annotations?.['verint.com/ven']);
  const { document, loading } = useSystemRegistryDocument(coordinates);

  if (!coordinates || loading || !document) return <>JSON</>;

  const count = document.venIssues?.length ?? 0;
  return (
    <>
      JSON
      {count > 0 ? (
        <span className={classes.tabBadge} style={{ color: '#ff5252' }}>({count})</span>
      ) : (
        <CheckCircleIcon
          className={classes.tabBadge}
          style={{ color: '#4caf50', fontSize: 14, verticalAlign: 'middle' }}
        />
      )}
    </>
  );
};

export const SystemRegistryJsonContent = () => {
  const { entity } = useEntity();
  const classes = useStyles();

  const [copied, setCopied] = useState(false);

  const coordinates = parseSystemVen(entity.metadata.annotations?.['verint.com/ven']);
  const { document, error, loading, reload } = useSystemRegistryDocument(coordinates);

  // Issues are portal-side analysis, not registry data — keep them out of the
  // rendered document and use them purely to annotate it.
  const { json, issues } = useMemo(() => {
    if (!document) return { json: null, issues: [] as VenIssue[] };
    const { venIssues = [], ...registryDocument } = document;
    return { json: JSON.stringify(registryDocument, null, 2), issues: venIssues };
  }, [document]);

  const lines = useMemo(() => (json ? annotateLines(json, issues) : []), [json, issues]);

  const copy = async () => {
    if (!json) return;
    await navigator.clipboard.writeText(json);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!coordinates) {
    return (
      <Box p={2}>
        <Typography variant="body2" color="textSecondary">
          This system has no <code>verint.com/ven</code> annotation, so it cannot be
          matched to a System Registry record.
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={2}>
      <Box mb={1} display="flex" alignItems="center" style={{ gap: 8 }}>
        <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={reload} disabled={loading}>
          Refresh
        </Button>
        <Button size="small" variant="outlined" startIcon={<FileCopyIcon />} onClick={copy} disabled={!json}>
          {copied ? 'Copied' : 'Copy'}
        </Button>
        <Box flex={1} />
        <Typography variant="caption" color="textSecondary">
          {coordinates.domain} / {coordinates.key}
        </Typography>
      </Box>

      {issues.length > 0 && !loading && (
        <Box className={classes.banner}>
          <ErrorOutlineIcon fontSize="small" />
          <Typography variant="body2">
            {issues.length} referenced {issues.length === 1 ? 'VEN is' : 'VENs are'} not in the
            System Registry.
          </Typography>
        </Box>
      )}

      {loading && (
        <Box display="flex" alignItems="center" style={{ gap: 8 }} p={2}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="textSecondary">
            Loading from the System Registry…
          </Typography>
        </Box>
      )}

      {error && !loading && (
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      )}

      {json && !loading && (
        <Paper variant="outlined">
          <Box className={classes.code}>
            {lines.map((line, index) => (
              <div
                // Line order is stable for a given render of the document.
                key={index}
                className={`${classes.line} ${line.error ? classes.errorLine : ''}`}
              >
                {line.text}
                {line.error && (
                  <span className={classes.errorText}>{'  ⟵ '}{line.error}</span>
                )}
              </div>
            ))}
          </Box>
        </Paper>
      )}
    </Box>
  );
};
